import { PALETTE } from "./format";

/**
 * Cell color scales for tables and pivots. Hues come from the categorical palette so they stay
 * CVD-checked in both modes; intensity is alpha over the surface, so text stays readable and the
 * printed number remains the primary encoding. The diverging scale is orange ↔ blue, not
 * red ↔ green, for the same reason.
 */
export const GRADIENT_SCHEMES = [
  { id: "none", label: "None" },
  { id: "blue", label: "Blue", slot: 0 },
  { id: "green", label: "Green", slot: 2 },
  { id: "orange", label: "Orange", slot: 1 },
  { id: "purple", label: "Purple", slot: 6 },
  { id: "diverging", label: "Orange ↔ Blue (diverging)", low: 1, high: 0 },
] as const;
export type GradientScheme = (typeof GRADIENT_SCHEMES)[number]["id"];

export const isGradientScheme = (v: unknown): v is GradientScheme => GRADIENT_SCHEMES.some((s) => s.id === v);

export interface GradientDomain {
  min: number;
  max: number;
  /** Diverging scales center here: 0 when the data spans it, else the midpoint. */
  mid: number;
}

/** Extent of the numeric values, or null when there are none. */
export function gradientDomain(values: Iterable<unknown>): GradientDomain | null {
  let min = Infinity, max = -Infinity;
  for (const v of values) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    min = Math.min(min, v);
    max = Math.max(max, v);
  }
  if (min === Infinity) return null;
  const mid = min < 0 && max > 0 ? 0 : (min + max) / 2;
  return { min, max, mid };
}

const MIN_ALPHA = 0.04;
const MAX_ALPHA = 0.42;
const mix = (hex: string, alpha: number) => `color-mix(in srgb, ${hex} ${Math.round(alpha * 1000) / 10}%, transparent)`;

/** Background for one cell, or undefined for no fill. Pure string output so it's easy to test. */
export function gradientFill(
  scheme: GradientScheme,
  value: unknown,
  domain: GradientDomain | null,
  mode: "light" | "dark",
  reverse = false,
): string | undefined {
  if (scheme === "none" || !domain || typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const s = GRADIENT_SCHEMES.find((x) => x.id === scheme);
  if (!s || !("slot" in s || "low" in s)) return undefined;
  const palette = PALETTE[mode];

  if ("low" in s) {
    const { min, max, mid } = domain;
    const above = value >= mid;
    const span = above ? max - mid : mid - min;
    const t = span > 0 ? Math.abs(value - mid) / span : 0;
    if (t === 0) return undefined;
    const high = above !== reverse;
    return mix(palette[high ? s.high : s.low]!, MIN_ALPHA + t * (MAX_ALPHA - MIN_ALPHA));
  }

  // Sequential scales anchor at 0 for non-negative data, so intensity reads as magnitude.
  const lo = Math.min(domain.min, 0);
  const span = domain.max - lo;
  let t = span > 0 ? (value - lo) / span : 1;
  if (reverse) t = 1 - t;
  return mix(palette[s.slot]!, MIN_ALPHA + t * (MAX_ALPHA - MIN_ALPHA));
}
